import { Injectable } from '@angular/core';
import { DailyLogOperationInstance } from '../../../models/projects/operation.models';
import { DateTime } from 'luxon';
import { TimeZoneService } from '../../time-zone/time-zone.service';
import { PtLuxonDate } from '../../../models/projects/date.models';

@Injectable()
export class DateParserService {

  constructor() {
  }

  // takes any object or array, searches for any DateTime objects, and then converts the objects to UTC
  removeOffsetFromLuxonDates(value: any): any {
    if (value && value.isLuxonDateTime) { // More robust check than instanceof
      return value.toUTC();
    }

    if (Array.isArray(value)) {
      // Map the array to ensure the cleaned values are actually stored back in the array
      for (let i = 0; i < value.length; i++) {
        value[i] = this.removeOffsetFromLuxonDates(value[i]);
      }
    } else if (value !== null && typeof value === 'object') {
      for (const property in value) {
        if (value.hasOwnProperty(property)) {
          value[property] = this.removeOffsetFromLuxonDates(value[property]);
        }
      }
    }

    return value;
  }

  removeOffsetFromLuxonDate(value: DateTime): DateTime {
    if (value instanceof DateTime) {
      return value.toUTC();
    } else {
      throw new Error('Given Type is not a Luxon DateTime object and cannot be converted to UTC.');
    }
  }

  // takes any object or array, searches for ISO date strings and converts them to DateTime objects, also applies timezone and sets offset
  // if convertTolocal === true, then we will look up the timezone for a given date, and apply the offset, timeZones can be given optionally to speed this up
  async deserializeDates(value: any, convertToLocal?: boolean, timeZoneService?: TimeZoneService): Promise<any> {
    if (typeof value === 'string') {
      return await this.deserializeDate(value, convertToLocal, timeZoneService);
    } else if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        value[i] = await this.deserializeDates(value[i], convertToLocal, timeZoneService);
      }
    } else if (value instanceof Object) {
      for (const property in value) {
        if (value.hasOwnProperty(property)) {
          value[property] = await this.deserializeDates(value[property], convertToLocal, timeZoneService);
        }
      }
    }

    return value;
  }

  // takes a ISO date string and converts to a DateTime object, applies in-app timezone
  async deserializeDate(value: any, convertToLocal?: boolean, timeZoneService?: TimeZoneService, offsetOverrideHours?: number): Promise<any> {
    if (value === '0001-01-01T00:00:00Z') {
      return DateTime.local();
    }

    if (!this.isDate(value)) {
    } else {
      // iso must be utc (+0), or we will be applying the offset twice
      let result = DateTime.fromISO(value);

      if (convertToLocal && timeZoneService) {
        if (offsetOverrideHours == null) {
          const tz = await timeZoneService.getEditorLocally({dateTime: result, dateAmbiguous: false});

          if (tz) {
            const offsetString = this.createLuxonOffsetString(tz.secondsOffsetFromUtc);
            // setZone() will not only set the offset of the DateTime, but also apply the offset to the date
            result = result.setZone(offsetString);
          }
        } else {
          const offsetString = this.createLuxonOffsetString(offsetOverrideHours * 60 * 60);
          result = result.setZone(offsetString);
        }
      }

      return result;
    }

    return value;
  }

  async deserializePtLuxonDate(ptLuxonDate: PtLuxonDate, timeZoneService?: TimeZoneService, offsetOverrideHours?: number): Promise<PtLuxonDate> {
    if (!ptLuxonDate || !ptLuxonDate.dateTime) {
      return new PtLuxonDate(null, true);
    }

    ptLuxonDate = PtLuxonDate.assignPtLuxonDate(ptLuxonDate);

    ptLuxonDate.dateTime = await this.deserializeDate(ptLuxonDate.dateTime, true, timeZoneService, offsetOverrideHours);

    // we may need to set ptLuxonDate.multipleOffsets.selectedOffset here, from the dateTime offset?

    return ptLuxonDate;
  }

  isDate(value: string): boolean {
    const dateFormat = /^(\d{4}|\+\d{6})(?:-(\d{2})(?:-(\d{2})(?:T(\d{2}):(\d{2}):(\d{2})\.?(\d*)(Z|([\-+])(\d{2}):(\d{2}))?)?)?)$/;
    return dateFormat.test(value);
  }

  // copy of dateService method, need one here to prevent circular dependency // should really refactor this to it's own service
  private createLuxonOffsetString(secondsOffset: number): string {
    let offsetString = '';

    if (secondsOffset > 0) {
      offsetString = 'utc+' + (secondsOffset / 3600); // convert seconds to hours
    } else if (secondsOffset < 0) {
      offsetString = 'utc' + (secondsOffset / 3600);
    } else {
      offsetString = 'utc';
    }

    return offsetString;
  }
}



// WEBPACK FOOTER //
// ./src/services/daily-log/daily-log-time/date-parser-service.ts